import React from 'react';
import { SnagScreen } from './SnagScreen';
import { logError } from '../../services/loggerService';

interface State {
  hasError: boolean;
}

/**
 * Top-level error boundary wrapping <App /> in main.tsx. Catches any
 * synchronous throw during React render/mount that would otherwise leave
 * the user staring at a blank page (which, after 3 reloads, triggers
 * Safari's "A problem repeatedly occurred" banner on iOS).
 *
 * Does NOT cover throws during ES module evaluation (those happen before
 * React mounts) — main.tsx has a separate try/catch for that path.
 */
export class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    try {
      logError(error, `AppErrorBoundary: ${info.componentStack ?? ''}`);
    } catch {
      // logger itself may be the thing that's broken — swallow
    }
  }

  render() {
    if (this.state.hasError) {
      return <SnagScreen />;
    }
    return this.props.children;
  }
}
